'use client'

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type Ref } from 'react'

import { FormattedPrice } from '@src/blocks/currency'
import FilledStar from '@src/components/product-card/icons/filledStar.inline.svg'
import { getProductLink } from '@src/components/product-card/product-card.helpers'
import { ProductTags } from '@src/components/product-card/product-image-section/product-tags/product-tags'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Icon } from '@src/quince-components/src'
import { Image } from '@src/quince-components/src/atoms/image/Image'
import Link from '@src/quince-components/src/atoms/link/Link'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { SnapScroll, type SnapScrollHandle } from '@src/quince-components/src/molecules/snap-scroll'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import { TAG_LOCATION, MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import type { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { SvdCardAddToBag } from './add-to-bag/svd-card-add-to-bag'
import {
  fireSvdGalleryView,
  fireSvdProductClick,
  fireSvdVariantSelect,
  useSvdProductCardImpression,
} from './svd-product-card.analytics'
import {
  getInitialSvdVariant,
  getSvdApplicableTags,
  getSvdImagesForGender,
  getSvdSocialProofLabels,
  getSvdSwatchVariants,
  reportSvdEmptyProductImages,
  shouldRenderSvdSwatches,
  SVD_SWATCH_DISPLAY_TYPE,
} from './svd-product-card.helpers'
import styles from './svd-product-card.module.scss'
import { SvdCardAddToWishlist } from './wishlist/svd-card-add-to-wishlist'

export interface SvdProductCardProps {
  product: SvdNativeProduct
  className?: string
  /** Rendered tile width (px) — forwarded to the image loader for right-sized requests. */
  maxWidth?: number
  /**
   * Opt-in mobile image carousel (mirrors the legacy card's `showMScroll` / `enableImageCarousel`).
   * Off by default — legacy passes `showMScroll={false}` in every product-list/landing section, so
   * the SVD card matches prod (single image on mobile) unless a section explicitly turns it on.
   */
  enableImageCarousel?: boolean
  /**
   * Analytics — all optional; sections that track pass these. Click tracking (`PRODUCT_CLICK`) needs
   * `productClickTrackConfig`; impression (`BULK_PRODUCT_CARD_VIEW`) needs `productViewTracker` (the
   * section must also `initializeBatchedAnalyticsEvent` for it). `productSearchObjectId` /
   * `productPosition` / `searchAlgoliaQueryId` drive the Algolia click on search surfaces only.
   */
  productClickTrackConfig?: TrackConfig
  productViewTracker?: string
  onClickCb?: () => void
  productSearchObjectId?: string
  productPosition?: number
  searchAlgoliaQueryId?: string
  /**
   * LCP — above-fold tiles opt in so the first image loads `eager` + `fetchPriority="high"`, and
   * `imageRef` exposes that element for LCP measurement. Off by default (lazy), matching prod.
   */
  eagerLoadImage?: boolean
  imageRef?: Ref<HTMLImageElement>
  /**
   * Controlled variant — a section can drive the selected variant (`selectedVariantId`) and observe
   * changes (`onVariantChange`). Pseudo-controlled: the card keeps its own state but re-syncs to the
   * id. Used by wishlist / post-order / reco surfaces.
   */
  selectedVariantId?: number
  onVariantChange?: (variant: SvdNativeCardVariant) => void
  /**
   * Custom footer + visibility flags (parity with the legacy card). `customFooter`, when set,
   * replaces the default brand/title/price/rating footer. The `hide*` flags gate individual pieces.
   */
  customFooter?: ReactNode
  hideTitle?: boolean
  hideReviews?: boolean
  hideTags?: boolean
  hideSwatches?: boolean
  /**
   * Wishlist heart. Rendered when the product allows it (`flags.wishlistEnabled`, non-bundle) and
   * `hideWishlist` is not set. Guests persist to localStorage only. `wishlistTrackingConfig` is
   * optional section-provided tracking context (search/position etc.).
   */
  hideWishlist?: boolean
  wishlistTrackingConfig?: Record<string, unknown>
  /**
   * Quick-add (bag) button — parity with the legacy/DS card. Rendered when the product allows it
   * (`flags.quickBuyEnabled`, non-bundle, a resolvable variant `spid`) on non-native views, unless
   * `hideBuyNow` is set; opens the shared quick-add modal (Path A: existing enrich flow). Desktop
   * hover-reveal by default. `forceShowBuyNow` keeps it always-visible (reco/cart surfaces);
   * `showBuyNowOnMobile` reveals it on mobile (surfaces where the tile href is disabled). Both default
   * off, so the currently-wired sections match prod (desktop hover-reveal only).
   */
  hideBuyNow?: boolean
  forceShowBuyNow?: boolean
  showBuyNowOnMobile?: boolean
  /**
   * Swatch-row overflow cap — parity with the legacy card's `optionLimit`. Defaults to
   * `MAX_NUMBER_OF_COLOR_OPTIONS` (5), the value every current render site already gets.
   */
  optionLimit?: number
  /**
   * Overflow ("+N") click handler — parity with the legacy card's `onShowMoreClick`. When provided,
   * the "+N" chip becomes a button that calls this instead of linking straight to `targetUrl`, so a
   * section can drive its own navigation (e.g. with a tracker query param) the way `ProductListSection`
   * does on its legacy branch. Sections that omit it keep the existing link-to-PDP default.
   */
  onShowMoreClick?: (params: { selectedVariant?: SvdNativeCardVariant }) => void
  /**
   * Explicit page-gender override for image filtering (parity with the legacy card's `gender` prop).
   * A variant image whose `description` is gender-marked (`'men'`/`'women'`) is dropped when it
   * doesn't match — omitted, this derives the context from the current URL instead, so most sections
   * never need to pass it. See `getSvdImagesForGender`.
   */
  gender?: GenderName
  /**
   * Section-supplied tracker string appended to the click-through link as a `?tracker=` query param
   * (parity with the legacy card's `ProductCardLink`, which threads its `tracker` prop into
   * `getProductLink`). Omitted by every current render site today — `productClickTrackConfig` still
   * carries the primary click-analytics attribution regardless; this only affects the query param on
   * the resulting PDP navigation.
   */
  tracker?: string
}

/**
 * SVD product card — renders directly off the native `SvdNativeProduct` (generic anchor swatches,
 * nullable `displayConfig`, per-variant `spid`/`url`). Image (hover-swap on desktop / opt-in mobile
 * carousel), title, brand, price (+ isRange), rating, swatch row + selection, tags, social proof,
 * click/impression/variant-select analytics, LCP hints, controlled variant, custom footer + hide
 * flags, and a native-webview navigation bridge. Reached via the section-level `shouldUseSvdCard()`
 * branch; the legacy card is untouched.
 */
export function SvdProductCard({
  product,
  className,
  maxWidth,
  enableImageCarousel = false,
  productClickTrackConfig,
  productViewTracker,
  onClickCb,
  productSearchObjectId,
  productPosition,
  searchAlgoliaQueryId,
  eagerLoadImage = false,
  imageRef,
  selectedVariantId,
  onVariantChange,
  customFooter,
  hideTitle = false,
  hideReviews = false,
  hideTags = false,
  hideSwatches = false,
  hideWishlist = false,
  wishlistTrackingConfig,
  hideBuyNow = false,
  forceShowBuyNow = false,
  showBuyNowOnMobile = false,
  optionLimit,
  onShowMoreClick,
  gender,
  tracker,
}: SvdProductCardProps) {
  const { t } = useTranslation('common')
  const { cardVariants, title, thirdPartyDetails, reviews } = product

  const [selectedVariant, setSelectedVariant] = useState<SvdNativeCardVariant | undefined>(() =>
    getInitialSvdVariant(cardVariants, selectedVariantId),
  )

  // Impression tracking: fire `BULK_PRODUCT_CARD_VIEW` once the card scrolls into view (0.85 vis,
  // matching the legacy card). The section owns the batched-analytics init; this only produces.
  const wrapperRef = useRef<HTMLElement>(null)
  const { isVisible } = useIsVisible(wrapperRef, 0.85)

  // Re-derive the selected variant when the data or the controlled id changes (pseudo-controlled,
  // mirroring the legacy card — the card keeps its own copy but a parent can drive it).
  useEffect(() => {
    setSelectedVariant(getInitialSvdVariant(cardVariants, selectedVariantId))
  }, [cardVariants, selectedVariantId])

  const brandName = thirdPartyDetails.brandDisplayName ?? thirdPartyDetails.brand
  // Parity with the legacy card: hovering swaps the image *source* to the variant's second image
  // (pointer devices only — touch never fires mouseenter). No fade/transition, matching legacy.
  const [isHovered, setIsHovered] = useState(false)
  const variantImages = getSvdImagesForGender(selectedVariant?.images ?? [], gender)
  const hoverImage = variantImages[1]
  const displayImage = isHovered && hoverImage ? hoverImage : variantImages[0]
  const { deviceType, isNativeView } = useDeviceInfo()
  const showImageCarousel = enableImageCarousel && deviceType === DEVICE_TYPES.MOBILE && variantImages.length > 1

  // Empty-image telemetry — parity with the legacy card's `reportEmptyProductImages` (fires when
  // gender filtering, or the wire itself, leaves a variant with zero images) and its `captureException`
  // fallback (fires when there's still no single image to show outside the carousel path). Effects,
  // not inline calls, so each only fires once per real change rather than on every render.
  useEffect(() => {
    if (variantImages.length === 0) {
      reportSvdEmptyProductImages(selectedVariant, gender, deviceType)
    }
  }, [variantImages.length, selectedVariant, gender, deviceType])

  useEffect(() => {
    if (!showImageCarousel && !displayImage) {
      captureException(new Error('No images found for selected variant in SVD product card'), {
        module: MODULES.PRODUCT_CARD,
        feature: FEATURES.IMAGE,
      })
    }
  }, [showImageCarousel, displayImage])

  const formattedRating = getFormattedAverageRating(reviews.averageRating)
  const showSwatches = !hideSwatches && shouldRenderSvdSwatches(product)
  // Cap the swatch row (parity with the legacy `optionLimit`); the rest collapse into a "+N" link.
  const { visible: swatchVariants, overflowCount } = getSvdSwatchVariants(
    cardVariants,
    optionLimit ?? MAX_NUMBER_OF_COLOR_OPTIONS,
  )
  const selectedAnchorLabel = selectedVariant?.displayConfig?.displayName
  // `selectedVariant.url` is the raw SAND/wire slug (relative, un-slugified anchor value, e.g.
  // `home/product?Color=Heather+Grey`) — it is NOT the canonical frontend PDP link and must never be
  // used directly (caused a real nav bug: wrong path segment, wrong color casing, missing `gender`).
  // `getProductLink` is the same shape-agnostic helper the legacy card uses for its click-through —
  // it slugifies the anchor value, derives `gender` from the current page's pathname, and appends the
  // section's `tracker` (if provided) the same way legacy's `ProductCardLink` does.
  const targetUrl = getProductLink(product.slug, selectedVariant?.displayConfig?.value, tracker)

  // Backend-driven tags (EDD / white-glove / new / sale, etc.) already arrive on the wire per
  // variant; an active promo's pills replace them unless the variant is sold out. Rendered via the
  // shared color-free ProductTags leaf.
  const applicableTags = getSvdApplicableTags(selectedVariant)
  // Quick-add (bag) gate — mirror the DS card: quick-buy allowed, not a bundle, a resolvable variant
  // spid (enrich fetch key), and not the native webview. Desktop-only reveal is handled in CSS.
  const showAddToBag =
    !hideBuyNow &&
    product.flags.quickBuyEnabled !== false &&
    product.type !== 'Bundle' &&
    selectedVariant?.spid != null &&
    !isNativeView
  // Social proof: stock urgency + recent-sales copy, backend-provided (empty for every product on
  // the wire today — design-pending).
  const socialProofLabels = getSvdSocialProofLabels(selectedVariant?.socialProofData)

  useSvdProductCardImpression({ productViewTracker, wrapperRef, isVisible, product, selectedVariant, applicableTags })

  // Swatch pick: fire variant-select analytics, update local state, notify a controlling parent.
  const handleSelectVariant = (variant: SvdNativeCardVariant) => {
    fireSvdVariantSelect({
      product,
      previousVariant: selectedVariant,
      nextVariant: variant,
      trackConfig: productClickTrackConfig,
    })
    setSelectedVariant(variant)
    onVariantChange?.(variant)
  }

  // Card click: fire click analytics + parent callback, then — inside the native (hybrid app)
  // webview — bridge navigation via postMessage instead of a browser nav (mirrors the legacy card).
  // postMessage first; only preventDefault on success, so a failure falls back to the browser nav.
  const handleCardClick = (event: ReactMouseEvent) => {
    fireSvdProductClick({
      product,
      selectedVariant,
      applicableTags,
      trackConfig: productClickTrackConfig,
      onClickCb,
      productSearchObjectId,
      productPosition,
      searchAlgoliaQueryId,
    })
    if (getIsNativeViewClient()) {
      try {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({
            action: 'NAVIGATION',
            url: window.location.href,
            newURL: targetUrl.split('?')[0],
            nextURL: targetUrl,
          }),
        )
        event.preventDefault()
      } catch (error) {
        // eslint-disable-next-line no-console -- surface bridge failures; Link nav then proceeds
        console.error('SVD card: native webview postMessage failed', error)
      }
    }
  }

  // Gallery analytics (C11): only meaningful once the carousel is on (`enableImageCarousel`). Reads
  // the just-settled slide index off the imperative handle — `SnapScroll`'s `handleOnScrollEvent`
  // already debounces to real index changes only, so this fires once per genuine swipe, not per
  // scroll tick.
  const carouselRef = useRef<SnapScrollHandle>(null)
  const handleCarouselSlideView = () => {
    const index = carouselRef.current?.getActiveIndex()
    const image = index != null ? variantImages[index] : undefined
    if (index == null || !image) return
    fireSvdGalleryView({ product, selectedVariant, applicableTags, trackConfig: productClickTrackConfig, image, index })
  }

  const defaultFooter = (
    <>
      {brandName && <div className={styles.brand}>{brandName}</div>}
      {!hideTitle && (
        <h2 className={styles.title} data-testid={TEST_LOCATORS.PRODUCT_CARD_TITLE}>
          {title}
        </h2>
      )}
      {/* a11y: announce the selected anchor (e.g. colour) to screen readers, generic over anchor name */}
      {selectedAnchorLabel && <span className={styles.srOnly}>{selectedAnchorLabel}</span>}
      <div className={styles.priceRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_PRICE}>
        {selectedVariant?.price.isRange && <span className={styles.fromPrefix}>{t('from')}</span>}
        <FormattedPrice amount={selectedVariant?.price.salePrice} />
      </div>
      {!hideReviews && formattedRating && (
        <div className={styles.rating}>
          <Icon className={styles.ratingIcon} iconSvg={FilledStar} />
          <span>{formattedRating}</span>
          {reviews.reviewCount > 0 && <span className={styles.ratingCount}>{`(${reviews.reviewCount})`}</span>}
        </div>
      )}
      {socialProofLabels.length > 0 && (
        <div className={styles.socialProof}>
          {socialProofLabels.map(label => (
            <span key={label} className={styles.socialProofItem}>
              {label}
            </span>
          ))}
        </div>
      )}
    </>
  )

  return (
    <article
      ref={wrapperRef}
      className={classnames(styles.svdProductCard, className)}
      data-testid={TEST_LOCATORS.PRODUCT_CARD}
    >
      <div
        className={styles.imageSection}
        onMouseEnter={() => {
          setIsHovered(true)
        }}
        onMouseLeave={() => {
          setIsHovered(false)
        }}
      >
        {/* Wishlist heart — sibling of the link (never nested in the anchor); absolutely positioned
            over the image. Lives inside `imageSection` (image-only bounds), NOT the outer `article` —
            sections that add card-level gutter padding for carousel spacing (e.g. ImageWithProductCarousel)
            would otherwise push a card-root-relative heart past the image's right edge. Rendered only
            when the product allows it + a section hasn't hidden it. */}
        {!hideWishlist && product.flags.wishlistEnabled && product.type !== 'Bundle' && (
          <SvdCardAddToWishlist
            product={product}
            selectedVariant={selectedVariant}
            wishlistTrackingConfig={wishlistTrackingConfig}
          />
        )}
        {/* Quick-add bag button — sibling of the link (never nested in the anchor), absolutely
            positioned bottom-right over the image. Desktop hover-reveal by default; the card owns the
            positioning/visibility classes and gates rendering via `showAddToBag`. */}
        {showAddToBag && (
          <SvdCardAddToBag
            product={product}
            selectedVariant={selectedVariant}
            applicableTags={applicableTags}
            productClickTrackConfig={productClickTrackConfig}
            productPosition={productPosition}
            className={classnames(
              styles.addToBag,
              forceShowBuyNow && styles.addToBagForceVisible,
              showBuyNowOnMobile && styles.addToBagMobile,
            )}
          />
        )}
        <Link href={targetUrl} className={styles.link} onClick={handleCardClick}>
          <div className={styles.imageContainer}>
            {showImageCarousel ? (
              // Remount (key) on variant change so the carousel resets to the first image.
              <SnapScroll
                ref={carouselRef}
                key={selectedVariant?.variantId}
                id={`svd-card-images-${product.productId}-${selectedVariant?.variantId ?? 0}`}
                spaceBetween={0}
                handleOnScrollEvent={handleCarouselSlideView}
              >
                {variantImages.map((image, imageIndex) => {
                  const isFirstEager = imageIndex === 0 && eagerLoadImage
                  return (
                    <Image
                      key={image.id}
                      ref={imageIndex === 0 ? imageRef : undefined}
                      url={image.url}
                      alt={image.alt || title}
                      title={image.alt || title}
                      id={image.id}
                      height={image.height}
                      width={image.width}
                      maxWidth={maxWidth}
                      loading={isFirstEager ? 'eager' : 'lazy'}
                      fetchPriority={isFirstEager ? 'high' : undefined}
                      className={styles.image}
                    />
                  )
                })}
              </SnapScroll>
            ) : displayImage ? (
              <Image
                ref={imageRef}
                url={displayImage.url}
                alt={displayImage.alt || title}
                title={displayImage.alt || title}
                id={displayImage.id}
                height={displayImage.height}
                width={displayImage.width}
                maxWidth={maxWidth}
                loading={eagerLoadImage ? 'eager' : 'lazy'}
                fetchPriority={eagerLoadImage ? 'high' : undefined}
                className={styles.image}
              />
            ) : (
              // Always render an image slot, even with no data, so a genuinely empty variant
              // degrades to a blank tile rather than collapsing the image section entirely — but NOT
              // via `<Image url="">`: an empty `url` doesn't match `IMAGE_DOMAIN_NAME`, so `Image`
              // takes its native-`<img>` branch and emits a real `src=""` that fetches the page URL and
              // shows a broken-image glyph. `role="img"` + `aria-label` keep the card's accessible name
              // without a network request — same pattern as the carousel's deferred-slide box
              // (`product-image-section.tsx`'s `<div role="img" aria-label={...} />`). `.image`'s
              // `width: 100%` is enough for sizing: `.imageContainer` is a flex container, so its only
              // child stretches to fill the height too, same as it does for a real `<Image>`.
              <div role="img" aria-label={title} className={styles.image} />
            )}
          </div>
          {!hideTags && applicableTags.length > 0 && (
            <ProductTags applicableTags={applicableTags} tagLocation={TAG_LOCATION.PRODUCT_CARD} />
          )}
        </Link>
      </div>

      {showSwatches && (
        <div className={styles.swatchRow} data-testid={TEST_LOCATORS.PRODUCT_CARD_SELECTED_COLOR}>
          {swatchVariants.map(variant => {
            const config = variant.displayConfig
            if (!config) return null
            const isSelected = variant.variantId === selectedVariant?.variantId
            // Render per swatch mode, driven by the contract's `displayType`: TEXT → a text chip,
            // IMAGE → an image swatch, HEX_COLOR → a colour chip via `backgroundColor`.
            const isTextSwatch = config.displayType === SVD_SWATCH_DISPLAY_TYPE.TEXT
            const isImageSwatch = config.displayType === SVD_SWATCH_DISPLAY_TYPE.IMAGE
            return (
              <button
                key={variant.variantId}
                type="button"
                aria-label={config.displayName}
                aria-pressed={isSelected}
                className={classnames(styles.swatch, {
                  [styles.swatchText ?? '']: isTextSwatch,
                  [styles.swatchSelected ?? '']: isSelected,
                  [styles.swatchBordered ?? '']: Boolean(config.addBorder),
                })}
                style={!isTextSwatch && config.hexCode ? { backgroundColor: config.hexCode } : undefined}
                onClick={() => {
                  handleSelectVariant(variant)
                }}
              >
                {isTextSwatch && (config.displayName || config.value)}
                {isImageSwatch && config.imgURL && <img src={config.imgURL} alt={config.displayName} />}
              </button>
            )
          })}
          {overflowCount > 0 &&
            (onShowMoreClick ? (
              <button
                type="button"
                className={styles.swatchOverflow}
                onClick={() => {
                  onShowMoreClick({ selectedVariant })
                }}
              >
                {`+${overflowCount}`}
              </button>
            ) : (
              <Link href={targetUrl} className={styles.swatchOverflow}>
                {`+${overflowCount}`}
              </Link>
            ))}
        </div>
      )}

      <Link href={targetUrl} className={styles.details} onClick={handleCardClick}>
        {customFooter ?? defaultFooter}
      </Link>
    </article>
  )
}
